import React, { useState, useEffect } from 'react';
import { ArrowRight, Sun, Car, Home  } from 'lucide-react'; // Added Zap icon
import { Link } from 'react-router-dom';

interface FeatureCardProps {
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  description: string;
  gradient: string;
  iconColor: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  subtitle,
  description,
  gradient,
  iconColor
}) => (
  <div className={`bg-gradient-to-br ${gradient} p-4 rounded-xl transition-all hover:scale-[1.02] hover:shadow-lg duration-300 flex-1 min-h-0 border border-white/20`}
    aria-labelledby={`${title.toLowerCase().replace(/\s/g, '-')}-title`}
    aria-describedby={`${title.toLowerCase().replace(/\s/g, '-')}-description`}>
    <div className="flex items-center gap-4 h-full">
      <div className="flex-shrink-0">
        {React.cloneElement(icon, { className: `h-12 w-12 ${iconColor}` })}
      </div>
      <div className="flex-1 min-w-0">
        <h4 id={`${title.toLowerCase().replace(/\s/g, '-')}-title`} className="font-bold text-gray-900 text-lg mb-1 leading-tight">
          {title}
        </h4>
        <h5 className="font-semibold text-gray-700 text-base mb-2 leading-snug">{subtitle}</h5>
        <p id={`${title.toLowerCase().replace(/\s/g, '-')}-description`} className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const BackgroundElements: React.FC = () => (
  <>
    {/* Background gradient - softened and extended */}
    <div className="absolute inset-0 bg-gradient-to-br from-wenergie-blue/5 via-sky-50 to-wenergie-orange/5 -z-10" />

    {/* Animated sun/energy blob - larger and more subtle */}
    <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-wenergie-orange opacity-10 animate-pulse -z-10 filter blur-3xl" />
    <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full bg-wenergie-blue opacity-10 animate-pulse delay-500 -z-10 filter blur-3xl" />
  </>
);

const Hero: React.FC = () => {
  // Array of CTA texts that will rotate
  const ctaTexts = [
    "Join the Community",
    "Start Your Journey",
    "Switch to Wenergie",
    "Get Started Today",
    "Join the Revolution"
  ];

  // State to track the current CTA text
  const [currentCtaIndex, setCurrentCtaIndex] = useState(0);

  // Rotate through CTA texts automatically
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCtaIndex((prevIndex) => (prevIndex + 1) % ctaTexts.length);
    }, 3500); // Slightly longer interval for better readability

    return () => clearInterval(interval);
  }, [ctaTexts.length]); // Added dependency for clarity

  const features = [
    {
      icon: <Sun />,
      title: "Homes with Solar panels",
      subtitle: "Earn more from your solar panels",
      description: "Make every ray count! Optimize uptime and earn rewards for every unit of clean energy produced!",
      gradient: "from-orange-50 to-orange-100",
      iconColor: "text-orange-500"
    },
    {
      icon: <Car />,
      title: "For EVs",
      subtitle: "Charge clean, earn more",
      description: "Charge your electric vehicle when the sun shines — and get rewarded for contributing to a greener grid.",
      gradient: "from-blue-50 to-blue-100",
      iconColor: "text-blue-500"
    },
    {
      icon: <Home />,
      title: "General Home Plan",
      subtitle: "Join the Revolution — no panels needed",
      description: "Whether you're renting or own an apartment, with or without solar panels, the Wenergie network helps you access clean energy and earn rewards.",
      gradient: "from-green-50 to-green-100",
      iconColor: "text-green-500"
    }
  ];

  return (
    <section id="home" className="relative pt-24 pb-20 lg:min-h-screen flex items-center overflow-hidden">
      <BackgroundElements />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10"> {/* Added z-10 to container */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20"> {/* Increased gap for better spacing */}

          {/* Left Content - Hero Text */}
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6 animate-fade-in-up"> {/* Increased font-weight, added animation */}
              <span className="block mb-2">Locally Powered.</span>
              <span className="text-wenergie-blue block mb-2">Globally Connected.</span> {/* Capitalized Connected */}
              <span className="text-wenergie-orange block">Future Ready.</span> {/* Capitalized Ready */}
            </h1>

            <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up delay-200"> {/* Added animation and delay */}
              Wenergie is a next-generation electricity company making your power{' '}
              <span className="font-semibold text-wenergie-blue">clean</span>,{' '}
              <span className="font-semibold text-wenergie-orange">reliable</span>,{' '}
              <span className="font-semibold text-emerald-600">and rewarding</span>. {/* Changed 'rewarding' to 'and rewarding' for better flow */}
            </p>

            <Link
              to="/plans"
              className="inline-flex items-center justify-center px-8 py-3 bg-wenergie-blue text-white rounded-full font-medium hover:bg-wenergie-blue/90 transition duration-300 shadow-lg shadow-wenergie-blue/20 group transform hover:-translate-y-1 animate-fade-in-up delay-400" // Added more hover effects and animation
            >
              {ctaTexts[currentCtaIndex]}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Right Content - Horizontal Feature Cards */}
          <div className="w-full lg:w-1/2 relative flex justify-center items-center"> {/* Centered content */}
            <div className="relative bg-white bg-opacity-90 backdrop-blur-md rounded-2xl shadow-xl p-6 overflow-hidden max-h-[600px] min-h-[520px] flex flex-col w-full lg:w-[90%] border border-gray-100 animate-fade-in-right"> {/* Adjusted width, improved backdrop-blur, added animation */}
              {/* Decorative element - moved and re-imagined */}
              <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-wenergie-orange rounded-full opacity-10 filter blur-2xl" />
              <div className="absolute -right-10 -top-10 w-48 h-48 bg-wenergie-blue rounded-full opacity-10 filter blur-2xl" />

              <h3 className="text-2xl font-bold text-gray-900 mb-6 relative z-10 text-center">
                How It Works: A Plan for Everyone
              </h3>

              {/* Horizontal Feature Cards Stack */}
              <div className="flex-1 flex flex-col justify-between space-y-4 min-h-0">
                {features.map((feature, index) => (
                  <FeatureCard key={index} {...feature} />
                ))}
              </div>

              {/* Live Network Status */}
              <div className="mt-6 text-center pt-4 flex-shrink-0 relative z-10 border-t border-gray-100">
                <div className="inline-flex items-center text-wenergie-blue font-medium text-sm">
                  <span className="relative flex h-3 w-3 mr-2"> {/* Slightly larger ping indicator */}
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wenergie-blue opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-wenergie-blue" />
                  </span>
                  <span>Live energy network active in <span className="font-bold">Bengaluru</span> </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

/* Add these keyframes to your CSS file (e.g., index.css or global.css) */
/*
@keyframes fadeInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInFromRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in-up {
  animation: fadeInFromBottom 0.8s ease-out forwards;
  opacity: 0; // Ensures it's hidden before animation starts
}

.animate-fade-in-up.delay-200 {
  animation-delay: 0.2s;
}

.animate-fade-in-up.delay-400 {
  animation-delay: 0.4s;
}

.animate-fade-in-right {
  animation: fadeInFromRight 0.8s ease-out forwards;
  opacity: 0;
}
*/